// Formatters and helpers for Profit Tracker.

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const nf0 = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const nf2 = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const money0 = (n) => {
  const v = Number(n) || 0;
  return (v < 0 ? "-$" : "$") + nf0.format(Math.abs(v));
};
export const money2 = (n) => {
  const v = Number(n) || 0;
  return (v < 0 ? "-$" : "$") + nf2.format(Math.abs(v));
};

const pad = (n) => String(n).padStart(2, "0");
export const toISO = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Week definition: Tuesday -> Monday (7 days inclusive).
// Given (year, month1to12, weekNumber1to5) return { fromDate, toDate } as Date.
export function weekRange(year, month1to12, weekNumber) {
  const start = new Date(year, month1to12 - 1, 1);
  // advance to first Tuesday of month
  while (start.getDay() !== 2) start.setDate(start.getDate() + 1);
  start.setDate(start.getDate() + (weekNumber - 1) * 7);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { fromDate: start, toDate: end };
}

// Build every week in a month as [{ id, weekNumber, from, to, label }]
export function weeksInMonth(year, month1to12) {
  const weeks = [];
  const first = new Date(year, month1to12 - 1, 1);
  let d = new Date(first);
  while (d.getDay() !== 2) d.setDate(d.getDate() + 1);
  let wn = 1;
  while (d.getMonth() === month1to12 - 1) {
    const from = new Date(d);
    const to = new Date(d);
    to.setDate(to.getDate() + 6);
    weeks.push({
      id: `W${wn}`,
      weekNumber: wn,
      from,
      to,
      label: formatRange(from, to),
    });
    d.setDate(d.getDate() + 7);
    wn += 1;
  }
  return weeks;
}

// Display-only: shift both ends back 1 day so the label matches how ATM Records
// and GM Profit (LizAccounting) show accounting weeks. The underlying fromDate
// / toDate stay Tuesday–Monday so data fetching is unaffected.
function formatRange(from, to) {
  const dFrom = new Date(from);
  dFrom.setDate(dFrom.getDate() - 1);
  const dTo = new Date(to);
  dTo.setDate(dTo.getDate() - 1);
  const sameMonth = dFrom.getMonth() === dTo.getMonth();
  const fm = MONTHS_SHORT[dFrom.getMonth()];
  const tm = MONTHS_SHORT[dTo.getMonth()];
  if (sameMonth) {
    return `${fm} ${dFrom.getDate()}–${dTo.getDate()}`;
  }
  return `${fm} ${dFrom.getDate()} – ${tm} ${dTo.getDate()}`;
}

// Last completed accounting week (the most recent week whose toDate has already
// passed). Mirrors getLastCompletedAccountingWeek in ATMReceipt / LizAccounting
// so the Profit Tracker defaults to the same selection as those screens.
export function currentWeekInfo(now = new Date()) {
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  for (let i = 0; i < 12; i++) {
    const weeks = weeksInMonth(y, m);
    for (let j = weeks.length - 1; j >= 0; j--) {
      const w = weeks[j];
      if (w.to < now) return { year: y, month: m, week: w };
    }
    m -= 1;
    if (m < 1) { m = 12; y -= 1; }
  }
  return null;
}

// Partner math for a single location.
// Matches the GM profit rule used by LizAccounting / WeeklyProfit:
//   1. Each manager takes max(0, net - threshold) * pct / 100
//   2. Owners share the remainder, normalized by their combined percentage
export function computeShare(location, investor) {
  if (!location || !investor) return 0;
  const net = Number(location.netProfit) || 0;
  const investors = location.investors || [];

  if (investor.isManager) {
    const t = Number(investor.profitThreshold) || 0;
    return Math.max(0, net - t) * (Number(investor.percentage) || 0) / 100;
  }

  const managerTotal = investors
    .filter((i) => i.isManager)
    .reduce((acc, m) => {
      const t = Number(m.profitThreshold) || 0;
      return acc + Math.max(0, net - t) * (Number(m.percentage) || 0) / 100;
    }, 0);
  const remaining = Math.max(0, net - managerTotal);
  const ownerPct = investors
    .filter((i) => !i.isManager)
    .reduce((s, o) => s + (Number(o.percentage) || 0), 0);
  if (!ownerPct) return 0;
  return remaining * (Number(investor.percentage) || 0) / ownerPct;
}

// Sum of manager deductions for a location.
export function managerCut(location) {
  const net = Number(location?.netProfit) || 0;
  return (location?.investors || [])
    .filter((i) => i.isManager)
    .reduce((acc, m) => {
      const t = Number(m.profitThreshold) || 0;
      return acc + Math.max(0, net - t) * (Number(m.percentage) || 0) / 100;
    }, 0);
}

// Back-compat: existing callers still import `investorShare` by id.
export function investorShare(netProfit, investors, investorId) {
  const me = (investors || []).find((i) => i.investorId === investorId);
  if (!me) return 0;
  return computeShare({ netProfit, investors }, me);
}

// Sum of shares for a named partner across many weekly rows.
// Expects each row to have investors pre-annotated with { name, share } (as returned by GET_WEEK)
// or raw investors in which case we compute.
export function totalShareForName(rows, partnerName) {
  let total = 0;
  for (const row of rows || []) {
    for (const inv of row.investors || []) {
      if (inv.name === partnerName) {
        total += Number(inv.share ?? 0) || 0;
      }
    }
  }
  return total;
}

export const sum = (arr, sel = (x) => x) =>
  (arr || []).reduce((a, b) => a + (Number(sel(b)) || 0), 0);

export const newItemId = () =>
  `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

// Scroll the Profit Tracker back to the top of the page.
// It renders inside the app shell's scroll container (.shipment-background),
// not its own element, so "scroll to top" has to walk up and find whichever
// ancestor actually scrolls — setting .pt-scroll's scrollTop does nothing.
// Called on every screen change (opening a location, going back, changing
// week/tab) so the new view starts at the top instead of inheriting the
// previous one's scroll position.
export function scrollPtToTop() {
  const root = document.querySelector(".pt-root");
  let node = root ? root.parentElement : null;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
      const target = node;
      target.scrollTop = 0;
      // Again next frame: the new screen may still be rendering, and a taller
      // outgoing screen can otherwise re-clamp the scroll position.
      window.requestAnimationFrame(() => {
        target.scrollTop = 0;
      });
      return;
    }
    node = node.parentElement;
  }
  window.scrollTo(0, 0);
}
